import { devLog, devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
  Tab,
  Tabs,
  Alert,
  Spinner,
  Modal,
  Table,
  Form,
  Accordion
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth, db, storage } from '../firebase';
import templateSubmissionService from '../services/templateSubmissionService';
import { collection, getDocs, query, where, serverTimestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import LearnerNavbar from '../components/LearnerNavbar';
import DocumentStatusSummary from '../components/DocumentStatusSummary';
import AttendanceView from '../components/AttendanceView';
import LearningPath from '../components/LearningPath';
import IssueReporter from '../components/IssueReporter';
import StudyTimer from '../components/StudyTimer';
import UpcomingLessons from '../components/UpcomingLessons';
import LinkIdNumberModal from '../components/LinkIdNumberModal';
import AddressModal from '../components/AddressModal';
import useLearnerData from '../hooks/useLearnerData';
import Leaderboard from '../components/Leaderboard';
import LearnerProgress from '../components/LearnerProgress';
import LearnerMeetingRecaps from '../components/LearnerMeetingRecaps';
import GoogleMeetLinks from '../components/GoogleMeetLinks';
import LearnerNotifications from '../components/LearnerNotifications';
import SessionRSVP from '../components/SessionRSVP';
import SessionPoll from '../components/SessionPoll';
import ComplianceCenter from '../components/ComplianceCenter';
import { TAKE_A_LOT_PROGRAM } from '../constants/salesCategories';
import NotificationService from '../services/notificationService';

const LearnerDashboard = () => {
  usePageTitle("My Dashboard");
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [showIdModal, setShowIdModal] = useState(false);
  const [idLinkedSuccess, setIdLinkedSuccess] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressCompletedSuccess, setAddressCompletedSuccess] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [submissionRequests, setSubmissionRequests] = useState([]);
  const [loadingSubmissionRequests, setLoadingSubmissionRequests] = useState(false);
  const [showSubmissionUploadModal, setShowSubmissionUploadModal] = useState(false);
  const [activeSubmissionRequest, setActiveSubmissionRequest] = useState(null);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [submittingSubmission, setSubmittingSubmission] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [showSubmissionHistoryModal, setShowSubmissionHistoryModal] = useState(false);
  const [selectedSubmissionHistory, setSelectedSubmissionHistory] = useState(null);
  const [requestSubmissionHistory, setRequestSubmissionHistory] = useState([]);
  const [templateSubmissionHistory, setTemplateSubmissionHistory] = useState([]);
  const [cohortMap, setCohortMap] = useState({});
  const [showTemplateResubmitModal, setShowTemplateResubmitModal] = useState(false);
  const [selectedTemplateSubmission, setSelectedTemplateSubmission] = useState(null);
  const [templateResubmitFile, setTemplateResubmitFile] = useState(null);
  const [templateResubmitProgress, setTemplateResubmitProgress] = useState(0);
  const [templateResubmitting, setTemplateResubmitting] = useState(false);
  const [templateResubmitError, setTemplateResubmitError] = useState('');

  const cohortIds = Array.from(new Set([
    userProfile?.cohortId,
    ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
    ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
  ].filter(Boolean)));
  const isInactiveLearner = userProfile?.isActive === false ||
    ['inactive', 'dropoff', 'dropped'].includes(String(userProfile?.status || '').toLowerCase());
  const canAccessCohortFeatures = !isInactiveLearner && cohortIds.length > 0;
  const [studyLogMinutes, setStudyLogMinutes] = useState(0);

  const {
    modules,
    progress,
    assignments,
    attendance,
    attendanceSummary,
    pendingSessions,
    documents,
    achievements,
    upcomingSessions,
    stats,
    loading,
    error,
    refresh
  } = useLearnerData(currentUser?.uid, userProfile);

  // ID number is optional — learners can add it via their profile at any time

  useEffect(() => {
    const loadCohorts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cohorts'));
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          map[docSnap.id] = data.name || data.cohortName || docSnap.id;
        });
        setCohortMap(map);
      } catch (err) {
        devError('Error loading cohorts:', err);
      }
    };

    loadCohorts();
  }, []);

  // Check if address is completed (runs AFTER ID number check)
  // Prompt ALL users with incomplete address to fill it in
  // This applies to new users and bulk-imported users alike
  useEffect(() => {
    if (userProfile && userProfile.idNumber && !loading) {
      // Check if address is complete (all required fields present)
      const hasCompleteAddress = userProfile.streetAddress &&
                                 userProfile.city &&
                                 userProfile.province &&
                                 userProfile.postalCode;

      // Also check if admin has flagged user to verify address (e.g., after bulk import)
      const needsAddressVerification = userProfile.needsAddressVerification === true;

      // Show address modal if address is incomplete OR verification is required
      if (!hasCompleteAddress || needsAddressVerification) {
        setShowAddressModal(true);
      }
    }
  }, [userProfile, loading]);

  useEffect(() => {
    if (loading) return;
    if (!pendingSessions || pendingSessions.length === 0) {
      setShowAttendanceModal(false);
      return;
    }

    const sessionIds = pendingSessions.map(session => session.id).join(',');
    const storageKey = `attendance_prompt_seen_${currentUser?.uid || 'user'}`;
    const lastSeen = sessionStorage.getItem(storageKey);
    if (lastSeen !== sessionIds) {
      sessionStorage.setItem(storageKey, sessionIds);
      setShowAttendanceModal(true);
    }
  }, [pendingSessions, loading, currentUser]);

  useEffect(() => {
    const loadSubmissionRequests = async () => {
      if (!currentUser) return;
      try {
        setLoadingSubmissionRequests(true);
        if (isInactiveLearner) {
          setSubmissionRequests([]);
          setRequestSubmissionHistory([]);
          setLoadingSubmissionRequests(false);
          return;
        }
        const learnerCohortIds = Array.from(new Set([
          userProfile?.cohortId,
          ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
          ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
        ].filter(Boolean)));

        if (learnerCohortIds.length === 0) {
          setSubmissionRequests([]);
          setRequestSubmissionHistory([]);
          return;
        }

        const requestsByArray = await getDocs(
          query(
            collection(db, 'submissionRequests'),
            where('cohortIds', 'array-contains-any', learnerCohortIds.slice(0, 10))
          )
        );
        const requestsBySingle = await getDocs(
          query(
            collection(db, 'submissionRequests'),
            where('cohortId', 'in', learnerCohortIds.slice(0, 10))
          )
        );
        const combinedDocs = new Map();
        [
          ...requestsByArray.docs,
          ...requestsBySingle.docs
        ].forEach((docSnap) => {
          combinedDocs.set(docSnap.id, docSnap);
        });
        const requestsRaw = Array.from(combinedDocs.values()).map((docSnap) => {
          const data = docSnap.data();
          const dueDate = data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null);
          return {
            id: docSnap.id,
            ...data,
            dueDate
          };
        });
        const requests = await Promise.all(requestsRaw.map(async (req) => {
          const submissionRef = doc(db, 'submissionRequests', req.id, 'submissions', currentUser.uid);
          const submissionSnap = await getDoc(submissionRef);
          if (submissionSnap.exists()) {
            const submissionData = submissionSnap.data();
            return {
              ...req,
              submissionStatus: submissionData.status || 'submitted',
              submissionFileName: Array.isArray(submissionData.files)
                ? submissionData.files.map((file) => file.fileName).join(', ')
                : (submissionData.fileName || ''),
              submissionUpdatedAt: submissionData.submittedAt?.toDate
                ? submissionData.submittedAt.toDate()
                : null
              ,
              previousVersions: Array.isArray(submissionData.previousVersions) ? submissionData.previousVersions : []
            };
          }
          return req;
        }));

        const openRequests = requests.filter(req => !req.status || req.status === 'open');
        openRequests.sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return a.dueDate - b.dueDate;
        });
        setSubmissionRequests(openRequests);

        const requestHistory = requests
          .filter(req => req.submissionStatus)
          .map(req => ({
            id: `submission-request-${req.id}`,
            title: req.title,
            type: 'Submission Request',
            status: req.submissionStatus,
            fileName: req.submissionFileName || '',
            submittedAt: req.submissionUpdatedAt,
            sourceId: req.id,
            previousVersions: req.previousVersions || []
          }));
        setRequestSubmissionHistory(requestHistory);
      } catch (err) {
        devError('Error loading submission requests:', err);
      } finally {
        setLoadingSubmissionRequests(false);
      }
    };

    loadSubmissionRequests();
  }, [userProfile?.cohortId, currentUser]);

  const getRequestCohortLabel = (request) => {
    if (!request) return '—';
    if (request.cohortId) {
      return cohortMap[request.cohortId] || 'Unknown Cohort';
    }
    if (Array.isArray(request.cohortIds) && request.cohortIds.length) {
      return request.cohortIds.map((id) => cohortMap[id] || 'Unknown Cohort').join(', ');
    }
    return '—';
  };

  useEffect(() => {
    const loadTemplateSubmissions = async () => {
      if (!currentUser) return;
      try {
        const submissionsSnapshot = await getDocs(
          query(collection(db, 'templateSubmissions'), where('learnerId', '==', currentUser.uid))
        );
        const submissions = submissionsSnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          const submittedAt = data.submittedAt?.toDate
            ? data.submittedAt.toDate()
            : (data.createdAt?.toDate ? data.createdAt.toDate() : null);
          return {
            id: `template-${docSnap.id}`,
            templateId: data.templateId || data.resourceId || data.templateRef || '',
            title: data.templateName || data.resourceTitle || data.title || 'Template Submission',
            type: 'Template Submission',
            status: data.status || 'submitted',
            fileName: data.fileName || '',
            submittedAt,
            previousVersions: Array.isArray(data.previousVersions) ? data.previousVersions : []
          };
        });
        setTemplateSubmissionHistory(submissions);
      } catch (err) {
        devError('Error loading template submissions:', err);
      }
    };

    loadTemplateSubmissions();
  }, [currentUser]);

  useEffect(() => {
    const loadStudyLogs = async () => {
      if (!currentUser) return;
      try {
        const logsSnapshot = await getDocs(
          query(collection(db, 'studyLogs'), where('userId', '==', currentUser.uid))
        );
        const minutes = logsSnapshot.docs.reduce((sum, docSnap) => {
          const data = docSnap.data();
          return sum + (data.minutes || 0);
        }, 0);
        setStudyLogMinutes(minutes);
      } catch (err) {
        devError('Error loading study logs:', err);
        setStudyLogMinutes(0);
      }
    };

    loadStudyLogs();
  }, [currentUser]);

  const isFileTypeAllowed = (file, requiredType) => {
    if (!requiredType || requiredType === 'any') return true;
    const name = file.name.toLowerCase();
    if (requiredType === 'pdf') return name.endsWith('.pdf');
    if (requiredType === 'doc') return name.endsWith('.doc') || name.endsWith('.docx');
    if (requiredType === 'xls') return name.endsWith('.xls') || name.endsWith('.xlsx');
    if (requiredType === 'image') return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
    return true;
  };

  const handleSubmissionUpload = async () => {
    if (!activeSubmissionRequest) return;
    if (isInactiveLearner) {
      setSubmissionError('Your account is inactive. Please contact your facilitator.');
      return;
    }
    if (!submissionFiles.length) {
      setSubmissionError('Please select at least one file to upload.');
      return;
    }
    const requiredType = activeSubmissionRequest.requiredFileType || 'any';
    const allowMultipleFiles = activeSubmissionRequest.allowMultipleFiles !== false;
    const maxFiles = Number.isFinite(Number(activeSubmissionRequest.maxFiles)) && Number(activeSubmissionRequest.maxFiles) > 0
      ? Number(activeSubmissionRequest.maxFiles)
      : (allowMultipleFiles ? null : 1);
    if (!allowMultipleFiles && submissionFiles.length > 1) {
      setSubmissionError('This request only allows a single file.');
      return;
    }
    if (maxFiles && submissionFiles.length > maxFiles) {
      setSubmissionError(`You can upload up to ${maxFiles} file(s) for this request.`);
      return;
    }
    const invalidFile = submissionFiles.find((file) => !isFileTypeAllowed(file, requiredType));
    if (invalidFile) {
      setSubmissionError(`File "${invalidFile.name}" must be ${requiredType.toUpperCase()}.`);
      return;
    }

    try {
      setSubmittingSubmission(true);
      setSubmissionError('');
      const filesToUpload = allowMultipleFiles ? submissionFiles : [submissionFiles[0]];
      const uploadedFiles = [];
      const totalFiles = filesToUpload.length;
      for (let i = 0; i < filesToUpload.length; i += 1) {
        const file = filesToUpload[i];
        const safeName = file.name.replace(/[^\w.\-]+/g, '_');
        const storageRef = ref(
          storage,
          `submissionRequests/${activeSubmissionRequest.id}/${currentUser.uid}/${Date.now()}_${safeName}`
        );
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed', (snapshot) => {
          const fileProgress = snapshot.totalBytes
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0;
          const overallProgress = Math.min(
            99,
            Math.round(((i + fileProgress) / totalFiles) * 100)
          );
          setSubmissionProgress(overallProgress);
        });

        await uploadTask;
        const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
        uploadedFiles.push({
          fileName: file.name,
          fileType: file.type || '',
          fileSize: file.size || 0,
          fileUrl
        });
      }

      const submissionRef = doc(db, 'submissionRequests', activeSubmissionRequest.id, 'submissions', currentUser.uid);
      const existingSnap = await getDoc(submissionRef);
      const existingData = existingSnap.exists() ? existingSnap.data() : null;
      const previousVersions = Array.isArray(existingData?.previousVersions) ? [...existingData.previousVersions] : [];
      if (existingData?.fileUrl || Array.isArray(existingData?.files)) {
        previousVersions.push({
          fileUrl: existingData.fileUrl || '',
          fileName: existingData.fileName || '',
          files: Array.isArray(existingData.files) ? existingData.files : [],
          submittedAt: existingData.submittedAt || null,
          status: existingData.status || 'submitted',
          comment: existingData.latestComment || '',
          replacedAt: new Date()
        });
      }

      await setDoc(submissionRef, {
        learnerId: currentUser.uid,
        learnerName: userProfile?.displayName || currentUser.email,
        learnerEmail: userProfile?.email || currentUser.email,
        cohortId: userProfile?.cohortId || '',
        fileName: uploadedFiles.map((file) => file.fileName).join(', '),
        fileType: uploadedFiles[0]?.fileType || '',
        fileSize: uploadedFiles.reduce((sum, file) => sum + (file.fileSize || 0), 0),
        fileUrl: uploadedFiles[0]?.fileUrl || '',
        files: uploadedFiles,
        fileUrls: uploadedFiles.map((file) => file.fileUrl),
        fileNames: uploadedFiles.map((file) => file.fileName),
        requiredFileType: activeSubmissionRequest.requiredFileType || 'any',
        status: 'submitted',
        latestComment: '',
        submissionCount: (existingData?.submissionCount || 0) + 1,
        previousVersions,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: 'learner'
      }, { merge: true });

      if (activeSubmissionRequest.createdById) {
        await NotificationService.notify(
          'submission',
          'New Submission',
          `${userProfile?.displayName || currentUser.email} submitted for "${activeSubmissionRequest.title}".`,
          activeSubmissionRequest.createdById,
          { category: 'submission_requests', additionalData: { requestId: activeSubmissionRequest.id } }
        );
      }

      setShowSubmissionUploadModal(false);
      setSubmissionFiles([]);
      setSubmissionProgress(0);
      setActiveSubmissionRequest(null);
    } catch (error) {
      devError('Error submitting file:', error);
      setSubmissionError('Failed to submit file. Please try again.');
    } finally {
      setSubmittingSubmission(false);
    }
  };

  const handleTemplateResubmit = async () => {
    if (!selectedTemplateSubmission) return;
    if (!templateResubmitFile) {
      setTemplateResubmitError('Please select a file to upload.');
      return;
    }

    try {
      setTemplateResubmitting(true);
      setTemplateResubmitError('');
      const submissionData = {
        templateId: selectedTemplateSubmission.templateId,
        templateTitle: selectedTemplateSubmission.title,
        learnerId: currentUser.uid,
        learnerName: userProfile?.displayName || currentUser.email,
        comments: '',
        dueDate: null
      };

      await templateSubmissionService.submitTemplate(
        submissionData,
        templateResubmitFile,
        (progress) => setTemplateResubmitProgress(progress)
      );

      const refreshed = await templateSubmissionService.getLearnerSubmissions(currentUser.uid);
      const mapped = refreshed.map((data) => ({
        id: `template-${data.id}`,
        templateId: data.templateId || data.resourceId || data.templateRef || '',
        title: data.templateName || data.resourceTitle || data.title || 'Template Submission',
        type: 'Template Submission',
        status: data.status || 'submitted',
        fileName: data.fileName || '',
        submittedAt: data.submittedAt,
        previousVersions: Array.isArray(data.previousVersions) ? data.previousVersions : []
      }));
      setTemplateSubmissionHistory(mapped);

      setShowTemplateResubmitModal(false);
      setSelectedTemplateSubmission(null);
      setTemplateResubmitFile(null);
      setTemplateResubmitProgress(0);
    } catch (error) {
      devError('Error resubmitting template:', error);
      setTemplateResubmitError('Failed to resubmit. Please try again.');
    } finally {
      setTemplateResubmitting(false);
    }
  };

  const moduleProgressMap = progress || {};
  const moduleList = modules || [];
  const salesReminderActive = useMemo(() => {
    const programHint = `${userProfile?.programName || userProfile?.program || userProfile?.cohortName || ''}`.toLowerCase();
    return programHint.includes('takealot') || programHint.includes('take a lot');
  }, [userProfile]);

  const currentModules = useMemo(() => {
    if (!moduleList.length) {
      return [];
    }

    return moduleList.filter((module) => {
      const moduleProgress = moduleProgressMap?.[module.id];
      return (moduleProgress?.progress || 0) < 100;
    });
  }, [moduleList, moduleProgressMap]);

  const userAchievements = useMemo(() => {
    const aggregated = [];
    Object.entries(moduleProgressMap || {}).forEach(([moduleId, moduleProgress]) => {
      const moduleAchievements = moduleProgress?.achievements || [];
      moduleAchievements.forEach((achievement) => {
        if (achievement && typeof achievement === 'object') {
          aggregated.push({
            moduleId,
            ...achievement
          });
        } else if (achievement) {
          aggregated.push({
            moduleId,
            id: achievement,
            title: achievement
          });
        }
      });
    });

    if (Array.isArray(achievements) && achievements.length > 0) {
      achievements.forEach((achievementId) => {
        if (!aggregated.find((item) => item.id === achievementId)) {
          aggregated.push({ id: achievementId, title: achievementId });
        }
      });
    }

    return aggregated.slice(0, 6);
  }, [moduleProgressMap, achievements]);

  const assignmentStats = useMemo(() => {
    const statsAccumulator = {
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      overdue: 0
    };

    if (!assignments || assignments.length === 0) {
      return statsAccumulator;
    }

    const now = new Date();

    assignments.forEach((assignment) => {
      const submission = assignment?.userSubmission;

      if (submission && ['completed', 'graded'].includes(submission.status)) {
        statsAccumulator.completed += 1;
        return;
      }

      if (submission && ['submitted', 'in_review', 'in_progress'].includes(submission.status)) {
        statsAccumulator.inProgress += 1;
      } else {
        statsAccumulator.notStarted += 1;
      }

      if (
        assignment.dueDate &&
        assignment.dueDate < now &&
        (!submission || !['completed', 'graded'].includes(submission.status))
      ) {
        statsAccumulator.overdue += 1;
      }
    });

    return statsAccumulator;
  }, [assignments]);

  const assignmentSubmittedCount = assignmentStats.completed + assignmentStats.inProgress;
  const assignmentTotalCount = assignmentStats.completed + assignmentStats.inProgress + assignmentStats.notStarted;

  const assignmentSubmissionHistory = (assignments || [])
    .filter((assignment) => {
      const status = assignment.userSubmission?.status;
      return ['submitted', 'completed', 'graded', 'in_review'].includes(status);
    })
    .map((assignment) => ({
      id: `assignment-${assignment.id}`,
      title: assignment.title || 'Assignment Submission',
      type: 'Assignment',
      status: assignment.userSubmission?.status || 'submitted',
      fileName: assignment.userSubmission?.fileName || '',
      submittedAt: assignment.userSubmission?.submittedAt?.toDate
        ? assignment.userSubmission.submittedAt.toDate()
        : null
    }));

  const allSubmittedItems = useMemo(() => {
    const combined = [
      ...assignmentSubmissionHistory,
      ...requestSubmissionHistory,
      ...templateSubmissionHistory
    ];
    return combined.sort((a, b) => {
      const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [assignmentSubmissionHistory, requestSubmissionHistory, templateSubmissionHistory]);
  const totalSubmissionCount = allSubmittedItems.length;

  const totalStudyTimeMinutes = useMemo(() => {
    if (!moduleProgressMap) {
      return studyLogMinutes;
    }

    return Object.values(moduleProgressMap).reduce((sum, module) => {
      const timeSpent = module.totalTimeSpent || module.timeSpent || 0;
      return sum + timeSpent;
    }, studyLogMinutes);
  }, [moduleProgressMap, studyLogMinutes]);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (logoutError) {
      // eslint-disable-next-line no-console
      devError('Failed to log out', logoutError);
    }
  };

  if (loading || !userProfile) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-5">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading your personalised dashboard...</p>
          </div>
        </Container>
      </>
    );
  }

  if (userProfile.role !== 'learner') {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-5">
          <Alert variant="danger">
            <h4>Access Denied</h4>
            <p>Only learners can access this dashboard.</p>
          </Alert>
        </Container>
      </>
    );
  }

  const overallProgress = stats?.overallProgress ?? 0;
  const attendancePresentCount = attendanceSummary?.presentCount ?? 0;
  const attendanceTotalCount = attendanceSummary?.totalSessions ?? 0;
  const documentsUploadedCount = documents?.documents ? documents.documents.length : 0;

  return (
    <>
      <LearnerNavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Welcome back, {userProfile?.displayName || currentUser?.email}</h1>
              <p className="mission-text mb-4">
                Track your progress, upcoming sessions, and key actions for your learning journey
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="light" className="me-2" onClick={() => navigate('/profile')}>
                View Profile
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container fluid className="px-4 py-4">

        {error && (
          <Alert
            variant="danger"
            className="mb-4"
          >
            <strong>We couldn&apos;t load all of your dashboard data.</strong>
            <div className="small mt-2">
              {error.message || 'Please refresh the page or try again later.'}
            </div>
          </Alert>
        )}

        {idLinkedSuccess && (
          <Alert variant="success" onClose={() => setIdLinkedSuccess(false)} dismissible className="mb-4">
            <strong>Success!</strong> Your ID number has been linked successfully. You can now upload documents.
          </Alert>
        )}

        {addressCompletedSuccess && (
          <Alert variant="success" onClose={() => setAddressCompletedSuccess(false)} dismissible className="mb-4">
            <strong>Success!</strong> Your address has been saved successfully.
          </Alert>
        )}

        {pendingSessions && pendingSessions.length > 0 && (
          <Alert variant="info" className="mb-4">
            Attendance confirmations are now handled by facilitators.
          </Alert>
        )}

        <Row>
          <Col md={4} className="mb-3">
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="icon-circle bg-primary text-white">
                    <i className="bi bi-bar-chart-line" />
                  </div>
                  <div>
                    <h3 className="mb-0">{overallProgress}%</h3>
                    <small className="text-muted">Overall Progress</small>
                  </div>
                  <ProgressBar
                    now={Math.min(100, Math.max(0, overallProgress))}
                    variant="primary"
                    style={{ height: '6px', flex: 1 }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="mb-3">
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="icon-circle bg-success text-white">
                    <i className="bi bi-check-circle" />
                  </div>
                  <div>
                    <h3 className="mb-0">{totalSubmissionCount}</h3>
                    <small className="text-muted">Total Submissions</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="mb-3">
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="icon-circle bg-warning text-white">
                    <i className="bi bi-clock-history" />
                  </div>
                  <div>
                    <h3 className="mb-0">{formatTime(totalStudyTimeMinutes)}</h3>
                    <small className="text-muted">Study Time Logged</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="mb-3">
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="icon-circle bg-info text-white">
                    <i className="bi bi-file-earmark-check" />
                  </div>
                  <div>
                    <h3 className="mb-0">{documentsUploadedCount}</h3>
                    <small className="text-muted">Documents Uploaded</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col lg={8}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-white">
                <h5 className="mb-0">Learning Momentum</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div style={{ flex: 1 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Assignments Submitted</span>
                      <Badge bg={assignmentStats.overdue > 0 ? 'danger' : 'success'}>
                        {assignmentStats.overdue > 0
                          ? `${assignmentStats.overdue} overdue`
                          : 'On track'}
                      </Badge>
                    </div>
                    <div className="fw-semibold">
                      {assignmentSubmittedCount}/{assignmentTotalCount} submitted
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Badge pill bg="success" className="px-3 py-2">
                    Completed: {assignmentStats.completed}
                  </Badge>
                  <Badge pill bg="info" className="px-3 py-2">
                    In Progress: {assignmentStats.inProgress}
                  </Badge>
                  <Badge pill bg="secondary" className="px-3 py-2">
                    Not Started: {assignmentStats.notStarted}
                  </Badge>
                  <Badge pill bg="danger" className="px-3 py-2">
                    Overdue: {assignmentStats.overdue}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-white">
                <h5 className="mb-0">Attendance Snapshot</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column justify-content-center text-center">
                <h2 className="display-4 mb-2">{attendancePresentCount}/{attendanceTotalCount}</h2>
                <p className="text-muted mb-3">
                  Sessions present (out of total marked sessions).
                </p>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/attendance')}>
                  View Detailed Attendance
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mt-4 shadow-sm border-0">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Assignments</h5>
            <Badge bg="primary">{assignments?.length || 0}</Badge>
          </Card.Header>
          <Card.Body>
            {assignments?.length > 0 ? (
              <Row>
                {assignments.map((assignment) => {
                  const status = assignment.userSubmission?.status || 'not_started';
                  const isComplete = ['completed', 'graded'].includes(status);
                  const isSubmitted = ['submitted', 'in_review', 'in_progress'].includes(status);
                  const isOverdue = assignment.dueDate &&
                    assignment.dueDate < new Date() &&
                    !isComplete;

                  return (
                    <Col md={6} lg={4} key={assignment.id} className="mb-3">
                      <Card className="h-100 border">
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <Card.Title className="h6 mb-0">
                              {assignment.title || assignment.name || 'Assignment'}
                            </Card.Title>
                            <Badge bg={isComplete ? 'success' : isSubmitted ? 'info' : isOverdue ? 'danger' : 'secondary'}>
                              {isComplete ? 'Completed' : isSubmitted ? 'Submitted' : isOverdue ? 'Overdue' : 'Not started'}
                            </Badge>
                          </div>
                          {assignment.description && (
                            <Card.Text className="small text-muted">
                              {assignment.description.substring(0, 120)}
                              {assignment.description.length > 120 ? '...' : ''}
                            </Card.Text>
                          )}
                          <div className="mt-auto">
                            <small className="text-muted d-block mb-3">
                              Due: {assignment.dueDate ? assignment.dueDate.toLocaleDateString() : 'No due date'}
                            </small>
                            <Button
                              size="sm"
                              variant={isComplete ? 'outline-success' : 'primary'}
                              onClick={() => navigate(`/learner/assignment/${assignment.id}`)}
                            >
                              {isComplete ? 'View Submission' : isSubmitted ? 'Review Submission' : 'Open Assignment'}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <p className="text-muted mb-0">No assignments have been assigned yet.</p>
            )}
          </Card.Body>
        </Card>

        {salesReminderActive && (
          <Row className="mt-4">
            <Col lg={12}>
              <Alert variant="info" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Weekly Sales Reminder:</strong> Please capture your weekly sales for the {TAKE_A_LOT_PROGRAM.name} programme.
                </div>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/take-a-lot-sales')}>
                  Log Sales
                </Button>
              </Alert>
            </Col>
          </Row>
        )}

        <Tabs defaultActiveKey="overview" id="learner-dashboard-tabs" className="mt-4">
          <Tab eventKey="overview" title="Overview">
            {/* Reminders, To-Do's, and Resources Section */}
            <Row className="mt-4">
              <Col lg={12}>
                {canAccessCohortFeatures ? (
                  <LearnerNotifications />
                ) : (
                  <Alert variant="light" className="mb-0">
                    Your account is not currently assigned to a cohort. Resources and reminders will appear once you are assigned.
                  </Alert>
                )}
              </Col>
            </Row>

            {canAccessCohortFeatures && (
            <Accordion defaultActiveKey={['submissions']} alwaysOpen className="mt-4">
              <Accordion.Item eventKey="submissions">
                <Accordion.Header>Submissions</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col lg={12}>
                      <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white">
                          <h5 className="mb-0">Submission Requests</h5>
                        </Card.Header>
                        <Card.Body>
                          {loadingSubmissionRequests ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : submissionRequests.length === 0 ? (
                            <Alert variant="light" className="mb-0">
                              No submission requests for your cohort right now.
                            </Alert>
                          ) : (
                            <Table responsive size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  <th>Title</th>
                                  <th>Cohort</th>
                                  <th>Due Date</th>
                                  <th>File Type</th>
                                  <th>Requested By</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {submissionRequests.map((request) => (
                                  <tr key={request.id}>
                                    <td>{request.title}</td>
                                    <td>{getRequestCohortLabel(request)}</td>
                                    <td>{request.dueDate ? request.dueDate.toLocaleDateString() : 'N/A'}</td>
                                    <td>{request.requiredFileType || 'Any'}</td>
                                    <td>{request.createdByName || 'Facilitator'}</td>
                                    <td>
                                      {request.submissionStatus ? (
                                        <Badge bg={request.submissionStatus === 'approved' ? 'success' :
                                          request.submissionStatus === 'rejected' ? 'danger' :
                                            request.submissionStatus === 'revision_requested' ? 'warning' : 'info'}>
                                          {request.submissionStatus.replace('_', ' ')}
                                        </Badge>
                                      ) : (
                                        <Badge bg="secondary">Not submitted</Badge>
                                      )}
                                    </td>
                                    <td>
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          setActiveSubmissionRequest(request);
                                          setSubmissionFiles([]);
                                          setSubmissionProgress(0);
                                          setSubmissionError('');
                                          setShowSubmissionUploadModal(true);
                                        }}
                                      >
                                        {request.submissionStatus ? 'Resubmit' : 'Upload'}
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col lg={12}>
                      <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white">
                          <h5 className="mb-0">Your Submissions</h5>
                        </Card.Header>
                        <Card.Body>
                          {allSubmittedItems.length === 0 ? (
                            <Alert variant="light" className="mb-0">
                              No submissions yet.
                            </Alert>
                          ) : (
                            <Table responsive size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  <th>Title</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                  <th>Submitted At</th>
                                  <th>File</th>
                                  <th>History</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allSubmittedItems.map((item) => (
                                  <tr key={item.id}>
                                    <td>{item.title}</td>
                                    <td>{item.type}</td>
                                    <td>
                                      <Badge bg={item.status === 'approved' || item.status === 'graded' ? 'success' :
                                        item.status === 'rejected' ? 'danger' :
                                          item.status === 'revision_requested' ? 'warning' :
                                            item.status === 'completed' ? 'success' : 'info'}>
                                        {item.status.replace('_', ' ')}
                                      </Badge>
                                    </td>
                                    <td>{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : '—'}</td>
                                    <td className="text-truncate" style={{ maxWidth: '220px' }}>
                                      {item.fileName || '—'}
                                    </td>
                                    <td>
                                      {item.previousVersions && item.previousVersions.length > 0 && (
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          onClick={() => {
                                            setSelectedSubmissionHistory(item);
                                            setShowSubmissionHistoryModal(true);
                                          }}
                                        >
                                          History
                                        </Button>
                                      )}
                                    </td>
                                    <td>
                                      {item.type === 'Template Submission' && (
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => {
                                            setSelectedTemplateSubmission(item);
                                            setTemplateResubmitFile(null);
                                            setTemplateResubmitProgress(0);
                                            setTemplateResubmitError('');
                                            setShowTemplateResubmitModal(true);
                                          }}
                                        >
                                          Resubmit
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="sessions">
                <Accordion.Header>Sessions & Meetings</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col lg={12}>
                      <SessionRSVP />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={12}>
                      <SessionPoll />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={12}>
                      <GoogleMeetLinks />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={12}>
                      <LearnerMeetingRecaps />
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="learning">
                <Accordion.Header>Learning Progress</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col lg={12}>
                      <LearnerProgress />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={12}>
                      <UpcomingLessons
                        sessions={upcomingSessions}
                        loading={loading}
                        modules={moduleList}
                        progress={moduleProgressMap}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={12}>
                      <DocumentStatusSummary
                        documentData={documents}
                        registrationDate={userProfile?.createdAt?.toDate?.() || new Date(userProfile?.createdAt || Date.now())}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col lg={8}>
                      <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">Current Modules</h5>
                          <Button size="sm" variant="link" onClick={() => navigate('/modules')}>
                            Browse All
                          </Button>
                        </Card.Header>
                        <Card.Body>
                          {currentModules.length > 0 ? (
                            <div className="row">
                              {currentModules.map((module) => (
                                <Col md={6} key={module.id} className="mb-3">
                                  <Card
                                    className="h-100 border-0 shadow-sm"
                                    role="button"
                                    onClick={() => navigate(`/learner/module/${module.id}`)}
                                  >
                                    <Card.Body>
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <h5 className="mb-1">{module.moduleName || module.title}</h5>
                                          <small className="text-muted">
                                            {module.description ? module.description.substring(0, 80) : 'No description provided.'}
                                            {module.description && module.description.length > 80 ? '…' : ''}
                                          </small>
                                        </div>
                                        <Badge bg="secondary">{module.difficulty || 'Module'}</Badge>
                                      </div>
                                      <div className="mt-3">
                                        <div className="d-flex justify-content-between small text-muted mb-1">
                                          <span>Progress</span>
                                          <span>{moduleProgressMap?.[module.id]?.progress || 0}%</span>
                                        </div>
                                        <ProgressBar
                                          now={moduleProgressMap?.[module.id]?.progress || 0}
                                          variant={moduleProgressMap?.[module.id]?.progress >= 100 ? 'success' : 'primary'}
                                        />
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center text-muted py-4">
                              <p className="mb-0">You haven&apos;t started any modules yet.</p>
                              <Button variant="primary" className="mt-3" onClick={() => navigate('/modules')}>
                                Explore Modules
                              </Button>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <StudyTimer />
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            )}
          </Tab>

          <Tab eventKey="learning-path" title="Learning Path">
            <Row className="mt-4">
              <Col md={8}>
                  <LearningPath
                    modules={moduleList}
                    progress={moduleProgressMap}
                    loading={loading}
                    moduleLinkBase="/learner/module"
                  />
              </Col>
              <Col md={4}>
                <DocumentStatusSummary
                  documentData={documents}
                  registrationDate={userProfile?.createdAt?.toDate?.() || new Date(userProfile?.createdAt || Date.now())}
                />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="documents" title="Compliance Centre">
            <Row className="mt-4">
              <Col lg={12}>
                <ComplianceCenter
                  documentData={documents}
                  registrationDate={userProfile?.createdAt?.toDate?.() || new Date(userProfile?.createdAt || Date.now())}
                />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="leaderboard" title="Leaderboard">
            <Row className="mt-4">
              <Col>
                <Leaderboard />
              </Col>
            </Row>
          </Tab>
        </Tabs>

        <Modal
          show={showSubmissionUploadModal}
          onHide={() => {
            if (!submittingSubmission) {
              setShowSubmissionUploadModal(false);
            }
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Submit File</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {activeSubmissionRequest && (
              <div className="mb-3">
                <strong>{activeSubmissionRequest.title}</strong>
                <div className="text-muted small">
                  Due: {activeSubmissionRequest.dueDate ? activeSubmissionRequest.dueDate.toLocaleDateString() : 'N/A'}
                  {' • '}Required: {(activeSubmissionRequest.requiredFileType || 'any').toUpperCase()}
                </div>
              </div>
            )}
            <Form.Group>
              <Form.Label>
                Select file{activeSubmissionRequest?.allowMultipleFiles === false ? '' : '(s)'}
              </Form.Label>
              <Form.Control
                type="file"
                multiple={activeSubmissionRequest?.allowMultipleFiles !== false}
                onChange={(e) => setSubmissionFiles(Array.from(e.target.files || []))}
                disabled={submittingSubmission}
              />
              {activeSubmissionRequest?.allowMultipleFiles !== false && (
                <div className="small text-muted mt-1">
                  {activeSubmissionRequest?.maxFiles
                    ? `You can upload up to ${activeSubmissionRequest.maxFiles} file(s).`
                    : 'You can upload multiple files.'}
                </div>
              )}
            </Form.Group>
            {submissionFiles.length > 0 && (
              <div className="small text-muted mt-2">
                Selected: {submissionFiles.map((file) => file.name).join(', ')}
              </div>
            )}
            {submissionProgress > 0 && (
              <ProgressBar now={submissionProgress} className="mt-3" />
            )}
            {submissionError && (
              <Alert variant="danger" className="mt-3">
                {submissionError}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSubmissionUploadModal(false)}
              disabled={submittingSubmission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmissionUpload}
              disabled={submittingSubmission}
            >
              {submittingSubmission ? 'Uploading...' : 'Submit'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showSubmissionHistoryModal} onHide={() => setShowSubmissionHistoryModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Submission History</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedSubmissionHistory?.previousVersions?.length ? (
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Submitted At</th>
                    <th>Status</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSubmissionHistory.previousVersions.map((version, index) => (
                    <tr key={`history-${index}`}>
                      <td>
                        {version.submittedAt?.toDate
                          ? version.submittedAt.toDate().toLocaleString()
                          : version.submittedAt
                            ? new Date(version.submittedAt).toLocaleString()
                            : '—'}
                      </td>
                      <td>{version.status || 'submitted'}</td>
                      <td>
                        {Array.isArray(version.files) && version.files.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {version.files.map((file, fileIndex) => (
                              <a
                                key={`history-file-${index}-${fileIndex}`}
                                href={file.fileUrl || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.fileName || `File ${fileIndex + 1}`}
                              </a>
                            ))}
                          </div>
                        ) : version.fileUrl ? (
                          <a href={version.fileUrl} target="_blank" rel="noopener noreferrer">
                            {version.fileName || 'View'}
                          </a>
                        ) : (
                          version.fileName || '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="light" className="mb-0">No previous submissions.</Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubmissionHistoryModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showTemplateResubmitModal} onHide={() => setShowTemplateResubmitModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Resubmit Template</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTemplateSubmission && (
              <div className="mb-3">
                <strong>{selectedTemplateSubmission.title}</strong>
                <div className="text-muted small">
                  Status: {selectedTemplateSubmission.status?.replace('_', ' ') || 'submitted'}
                </div>
              </div>
            )}
            <Form.Group>
              <Form.Label>Select file</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setTemplateResubmitFile(e.target.files?.[0] || null)}
                disabled={templateResubmitting}
              />
            </Form.Group>
            {templateResubmitProgress > 0 && (
              <ProgressBar now={templateResubmitProgress} className="mt-3" />
            )}
            {templateResubmitError && (
              <Alert variant="danger" className="mt-3">
                {templateResubmitError}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowTemplateResubmitModal(false)}
              disabled={templateResubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTemplateResubmit}
              disabled={templateResubmitting}
            >
              {templateResubmitting ? 'Uploading...' : 'Resubmit'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Row className="mt-4">
          <Col lg={4}>
            <IssueReporter />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">My Achievements</h5>
                <Badge bg="primary">{userAchievements.length}</Badge>
              </Card.Header>
              <Card.Body>
                {userAchievements.length > 0 ? (
                  <Row>
                    {userAchievements.map((achievement, index) => (
                      <Col md={4} key={achievement.id || `achievement-${index}`} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                          <Card.Body>
                            <Badge bg="success" className="mb-2">
                              {achievement.status || 'Unlocked'}
                            </Badge>
                            <h5 className="mb-2">{achievement.title || achievement.name || 'Achievement'}</h5>
                            {achievement.description && (
                              <p className="text-muted small mb-2">{achievement.description}</p>
                            )}
                            {achievement.moduleId && (
                              <small className="text-muted">
                                Module: {moduleList.find((module) => module.id === achievement.moduleId)?.moduleName || achievement.moduleId}
                              </small>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center text-muted py-4">
                    <p className="mb-0">Keep learning to unlock achievements!</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <AttendanceView
              attendanceRecords={attendance}
              attendanceSummary={attendanceSummary}
              pendingSessions={pendingSessions}
              modules={moduleList}
              onAttendanceSubmitted={refresh}
            />
          </Col>
        </Row>
      </Container>

      {/* ID Number Linking Modal */}
      <LinkIdNumberModal
        show={showIdModal}
        onHide={() => setShowIdModal(false)}
        onSuccess={() => {
          setShowIdModal(false);
          setIdLinkedSuccess(true);
        }}
      />

      {/* Address Modal - shown after ID linking for new users */}
      <AddressModal
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
        onSuccess={() => {
          setShowAddressModal(false);
          setAddressCompletedSuccess(true);
        }}
      />
    </>
  );
};

export default LearnerDashboard;
